import React, { useState } from 'react';
import {
  FileText, Search, Package, Tag, DollarSign, Image, BarChart3,
  CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronRight,
  Loader2, ExternalLink, Copy, ShieldCheck
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const StatCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    pink: 'bg-pink-50 text-pink-600 border-pink-100',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
      <div className={`p-2.5 rounded-xl border ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const CoverageBar = ({ label, count, total, pct }) => {
  const p = parseFloat(pct);
  const barColor = p >= 90 ? 'bg-green-500' : p >= 60 ? 'bg-yellow-500' : 'bg-red-500';
  const icon = p >= 90 ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : p >= 60 ? <AlertCircle className="w-4 h-4 text-yellow-500" /> : <XCircle className="w-4 h-4 text-red-500" />;
  return (
    <div className="flex items-center gap-3 py-2">
      {icon}
      <div className="flex-1">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-700 capitalize">{label}</span>
          <span className="text-sm text-gray-500">{count.toLocaleString()} / {total.toLocaleString()} ({pct}%)</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div className={`${barColor} h-2 rounded-full transition-all duration-500`} style={{ width: `${Math.min(p, 100)}%` }} />
        </div>
      </div>
    </div>
  );
};

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg"><Icon className="w-4 h-4 text-blue-600" /></div>
          <span className="font-semibold text-gray-800">{title}</span>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="p-4 pt-0 border-t border-gray-100">{children}</div>}
    </div>
  );
};

export default function XmlFeedAnalyzer() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const analyze = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/xml-feed/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ url: url.trim() })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Analysis failed');
      if (json.totalProducts === 0) throw new Error(json.error || 'No products found');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyField = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl text-white">
            <FileText className="w-6 h-6" />
          </div>
          XML Feed Analyzer
        </h1>
        <p className="text-gray-500 mt-1 ml-14">Paste your product feed URL to get a detailed analysis of your feed quality and content.</p>
      </div>

      {/* Input */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && analyze()}
              placeholder="https://example.com/products.xml"
              className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={analyze}
            disabled={loading || !url.trim()}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? 'Analyzing...' : 'Analyze Feed'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center gap-4">
          <div className="loading-logo-container">
            <img src="/logoicon.png" alt="Glowytics Logo" className="loading-logo" />
          </div>
          <p className="text-gray-500 text-sm">Fetching and analyzing your XML feed...</p>
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="space-y-6 animate-in fade-in duration-500">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard icon={Package} label="Total Products" value={data.totalProducts.toLocaleString()} color="blue" />
            <StatCard icon={Tag} label="Unique Brands" value={data.topBrands?.length || 0} color="purple" />
            <StatCard icon={BarChart3} label="Categories" value={data.topCategories?.length || 0} color="green" />
            {data.priceStats && (
              <StatCard icon={DollarSign} label="Avg Price" value={data.priceStats.avg.toFixed(2)} sub={`Min: ${data.priceStats.min.toFixed(2)} · Max: ${data.priceStats.max.toFixed(2)}`} color="orange" />
            )}
            {data.currencies?.length > 0 && (
              <StatCard icon={DollarSign} label="Currency" value={data.currencies.map(c => c.currency).join(', ') || '—'} color="pink" />
            )}
            <StatCard icon={Image} label="With Images" value={`${data.fieldCoverage.image.pct}%`} color="indigo" />
            <StatCard icon={ShieldCheck} label="With GTIN" value={`${data.fieldCoverage.gtin.pct}%`} color="green" />
            <StatCard icon={FileText} label="Fields Detected" value={data.allFields?.length || 0} color="blue" />
          </div>

          {/* Field Coverage */}
          <CollapsibleSection title="Field Coverage Analysis" icon={BarChart3} defaultOpen={true}>
            <div className="mt-3 space-y-1">
              {Object.entries(data.fieldCoverage).map(([field, info]) => (
                <CoverageBar key={field} label={field} count={info.count} total={data.totalProducts} pct={info.pct} />
              ))}
            </div>
          </CollapsibleSection>

          {/* Price Distribution */}
          {data.priceStats && (
            <CollapsibleSection title="Price Statistics" icon={DollarSign} defaultOpen={true}>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Minimum', value: data.priceStats.min.toFixed(2) },
                  { label: 'Maximum', value: data.priceStats.max.toFixed(2) },
                  { label: 'Average', value: data.priceStats.avg.toFixed(2) },
                  { label: 'Median', value: data.priceStats.median.toFixed(2) },
                ].map(s => (
                  <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                    <p className="text-lg font-bold text-gray-900">{s.value}</p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Availability */}
          {data.availabilities?.length > 0 && (
            <CollapsibleSection title="Availability Breakdown" icon={CheckCircle2}>
              <div className="mt-3 space-y-2">
                {data.availabilities.map(a => (
                  <div key={a.status} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700 capitalize">{a.status.replace(/_/g, ' ')}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">{a.count.toLocaleString()} products</span>
                      <span className="text-xs font-medium text-gray-400">({((a.count / data.totalProducts) * 100).toFixed(1)}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Top Brands */}
          {data.topBrands?.length > 0 && (
            <CollapsibleSection title={`Top Brands (${data.topBrands.length})`} icon={Tag}>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 text-gray-500 font-medium">#</th>
                      <th className="text-left py-2 px-3 text-gray-500 font-medium">Brand</th>
                      <th className="text-right py-2 px-3 text-gray-500 font-medium">Products</th>
                      <th className="text-right py-2 px-3 text-gray-500 font-medium">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topBrands.map((b, i) => (
                      <tr key={b.name} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                        <td className="py-2 px-3 font-medium text-gray-800">{b.name}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{b.count.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-gray-500">{((b.count / data.totalProducts) * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          )}

          {/* Top Categories */}
          {data.topCategories?.length > 0 && (
            <CollapsibleSection title={`Top Categories (${data.topCategories.length})`} icon={BarChart3}>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 text-gray-500 font-medium">#</th>
                      <th className="text-left py-2 px-3 text-gray-500 font-medium">Category</th>
                      <th className="text-right py-2 px-3 text-gray-500 font-medium">Products</th>
                      <th className="text-right py-2 px-3 text-gray-500 font-medium">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topCategories.map((c, i) => (
                      <tr key={c.name} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                        <td className="py-2 px-3 font-medium text-gray-800 max-w-md truncate">{c.name}</td>
                        <td className="py-2 px-3 text-right text-gray-600">{c.count.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-gray-500">{((c.count / data.totalProducts) * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          )}

          {/* Detected Fields */}
          {data.allFields?.length > 0 && (
            <CollapsibleSection title={`All Detected Fields (${data.allFields.length})`} icon={FileText}>
              <div className="mt-3 flex flex-wrap gap-2">
                {data.allFields.map(f => (
                  <button key={f} onClick={() => copyField(f)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-mono text-gray-700 transition-colors flex items-center gap-1.5">
                    {f} <Copy className="w-3 h-3 text-gray-400" />
                  </button>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Sample Products */}
          {data.sampleProducts?.length > 0 && (
            <CollapsibleSection title="Sample Products (First 5)" icon={Package}>
              <div className="mt-3 space-y-3">
                {data.sampleProducts.map((product, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-bold text-gray-500 mb-2">Product #{idx + 1}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries(product).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <span className="text-xs font-mono text-blue-600 whitespace-nowrap">{k}:</span>
                          <span className="text-xs text-gray-700 truncate">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Feed Meta */}
          <div className="text-xs text-gray-400 text-right flex items-center justify-end gap-4">
            <span>Analyzed at {new Date(data.analyzedAt).toLocaleString()}</span>
            <a href={data.feedUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-500 hover:text-blue-600">
              Open Feed <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
